import CancelChangesModal from '@/components/common/CancelChangesModal/cancelChangesModal';
import Spinner, { SpinnerSize } from '@/components/common/Spinner/Spinner';
import { IArtist } from '@/data/models/artist';
import {
  IPublisher,
  ITrack,
  IWriterComposer,
  TrackType,
} from '@/data/models/track';
import { IMixedField, ITrackMixed } from '@/data/models/trackMixed';
import { IValidationResults } from '@/data/models/validation';
import { useCatalogData } from '@/hooks/useCatalogData';
import { IMultipleTrackEdit } from '@/types/multiple-track-types';
import { isVideoFormat } from '@/utils/helper';
import React from 'react';
import { useTranslation } from 'react-i18next';
import '../../ProductDetailsEdit/ProductDetailsEdit.scss';
import ValidationErrors from '../../ProductView/ValidationErrors/ValidationErrors';
import {
  TrackArtistFormGroup,
  TrackAudioLanguageFormGroup,
  TrackCopyrightYearFormGroup,
  TrackDisplayArtistFormGroup,
  TrackDurationFormGroup,
  TrackGenreFormGroup,
  TrackIsrcFormGroup,
  TrackPhonographicCopyrightYearFormGroup,
  TrackPublishersFormGroup,
  TrackTitleFormGroup,
  TrackTypeFormGroup,
  TrackVersionFormGroup,
  TrackWriterComposerFormGroup,
} from './FormGroup';
import './TrackEditView.scss';
import { ICurrentTracksChangesGroup } from '@/types/product-current-changes-types';
import ChangeRequestIndicator from '@/components/common/ChangeRequestIndicator/ChangeRequestIndicator';
import { TimeUtilities } from '@/components/common/TimeUtilities';
import DismissibleErrorAlert from '@/components/common/ErrorAlert/DismissibleErrorAlert';
import { AlbumState } from '@/data/models/albumState';

export type TrackEditViewTemplateProps = {
  readonly?: boolean;
  disabled?: boolean;
  usesSides?: boolean;
  multipleTracksEdit?: IMultipleTrackEdit;
  isMultipleTrack?: boolean;
  areNewMultipleTracks?: boolean;
  multipleTracksCount?: number;
  formatName?: string | null;
  trackData: ITrack;
  baseMixedTrack?: ITrackMixed;
  showPopUp: boolean;
  onCancel: () => void;
  closePopUp: () => void;
  isSubmitting: boolean;
  showCancelChangesModal: boolean;
  setShowCancelChangesModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit: (closeModal: boolean) => void | Promise<unknown>;
  handleInput: (field: any, value: any) => void;
  handleArtist:
    | ((artistData: IArtist[]) => void)
    | ((artistData: IMixedField[]) => void);
  handleWriterComposer:
    | ((writerComposerData: IWriterComposer[]) => void)
    | ((writerComposerData: IMixedField[]) => void);
  handlePublisher:
    | ((publisherData: IPublisher[]) => void)
    | ((publisherData: IMixedField[]) => void);
  handleDuration?: (
    newDuration: number | undefined,
    isNewDurationValid: boolean
  ) => void;
  errorBanner?: boolean;
  errorMessages?: string[];
  validationErrors?: IValidationResults[];
  saveErrorMessage?: string | null;
  onSaveErrorClose?: () => void;
  isTrackValid: boolean;
  fieldHasErrors: (fieldName: string) => boolean;
  fieldValidationState?: (fieldName: string) => string | undefined;
  findErrorMessage: (field: string) => string;
  previousTrack?: () => void | Promise<void>;
  nextTrack?: () => void | Promise<void>;
  formatTrackLabel?: (usesSides: boolean, volumeNumber: number) => string;
  currentChanges?: ICurrentTracksChangesGroup;
  isChangeRequest?: boolean;
  albumState?: AlbumState;
};

const TrackEditViewTemplate = ({
  readonly,
  disabled,
  usesSides,
  multipleTracksEdit,
  isMultipleTrack,
  areNewMultipleTracks,
  multipleTracksCount,
  formatName,
  trackData,
  baseMixedTrack,
  showPopUp,
  onCancel,
  closePopUp,
  isSubmitting,
  showCancelChangesModal,
  setShowCancelChangesModal,
  handleSubmit,
  handleInput,
  handleArtist,
  handleWriterComposer,
  handlePublisher,
  handleDuration,
  errorBanner,
  errorMessages,
  validationErrors,
  saveErrorMessage,
  onSaveErrorClose,
  albumState,
  isTrackValid,
  fieldHasErrors,
  fieldValidationState,
  findErrorMessage,
  previousTrack,
  nextTrack,
  formatTrackLabel,
  currentChanges,
  isChangeRequest,
}: TrackEditViewTemplateProps) => {
  const { t } = useTranslation();
  const {
    genres,
    audioLanguages,
    queryGenres,
    queryAudioLanguages,
    isLoadingCatalogs,
  } = useCatalogData();

  const trackKey = isMultipleTrack
    ? 'multi'
    : `${trackData?.discOrSideNumber}-${trackData?.trackNumber}`;
  const showTrackType = isVideoFormat(formatName);

  const currentTrackChanges = currentChanges?.tracks.find(
    (t) => t.id === trackData.id
  );

  return (
    <>
      <CancelChangesModal
        onSubmit={() => handleSubmit(true)}
        onClose={closePopUp}
        onCancel={() => setShowCancelChangesModal(false)}
        show={showCancelChangesModal}
        titleText={
          !isMultipleTrack
            ? t('track.editTrackMetadata')
            : t('track.editMultipleTracks')
        }
        bodyText={
          !isMultipleTrack
            ? t('track.cancelMetadataChangesBody')
            : t('track.cancelMultipleTracksChangesBody')
        }
      />
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(true);
        }}
      >
        {showPopUp ? (
          <div
            data-testid="trackEdit"
            id="traclModal"
            className={`product-edit-mode-modal ${isMultipleTrack ? 'multi-track-edit-modal' : ''}`}
          >
            <div className="product-edit-mode-modal-content">
              <div className="product-edit-mode-modal-header">
                <button
                  className="btn-close"
                  type="button"
                  aria-label="Close"
                  onClick={onCancel}
                >
                  <i className="icon fas fa-times fa-lg" aria-hidden="true"></i>
                </button>
                <h2>
                  {!isMultipleTrack
                    ? `${!readonly ? t('track.title') + ' > ' + trackData?.title : t('track.viewDetails')}`
                    : `${t('track.editMultipleTracks')} (${multipleTracksCount} ${t('common.tracks')})`}
                </h2>
                <div className="edit-modal-spacer"></div>
              </div>
              <div
                className={`product-edit-mode-modal-body ${readonly ? 'readonly-modal' : ''}`}
              >
                {isSubmitting ? (
                  <Spinner cover={true} size={SpinnerSize.FullComponent} />
                ) : null}
                <div className="scroll-body">
                  <DismissibleErrorAlert
                    error={saveErrorMessage}
                    onClose={onSaveErrorClose ?? (() => undefined)}
                  />
                  {!isMultipleTrack && !readonly ? (
                    <ValidationErrors
                      errorBanner={errorBanner ?? false}
                      errors={errorMessages ?? []}
                      validationErrors={validationErrors ?? []}
                      region={'Edit'}
                    ></ValidationErrors>
                  ) : null}
                  <div className="edit-form">
                    {/* Title */}
                    <ChangeRequestIndicator
                      show={currentTrackChanges?.title.hasChanges}
                      displayValue={currentTrackChanges?.title.value}
                    ></ChangeRequestIndicator>
                    <TrackTitleFormGroup
                      isMultipleTrack={isMultipleTrack}
                      trackData={trackData}
                      handleInput={handleInput}
                      baseMixedTrack={baseMixedTrack}
                      areNewMultipleTracks={areNewMultipleTracks}
                      fieldHasErrors={fieldHasErrors}
                      findErrorMessage={findErrorMessage}
                      multipleTracksEdit={multipleTracksEdit}
                      readonly={readonly}
                      disabled={disabled}
                    />

                    {/* Version */}
                    <ChangeRequestIndicator
                      show={currentTrackChanges?.version.hasChanges}
                      displayValue={currentTrackChanges?.version.value}
                    ></ChangeRequestIndicator>
                    <TrackVersionFormGroup
                      isMultipleTrack={isMultipleTrack}
                      multipleTracksEdit={multipleTracksEdit}
                      trackData={trackData}
                      baseMixedTrack={baseMixedTrack}
                      readonly={readonly}
                      disabled={disabled}
                      areNewMultipleTracks={areNewMultipleTracks}
                      handleInput={handleInput}
                      fieldHasErrors={fieldHasErrors}
                      findErrorMessage={findErrorMessage}
                    />

                    {/* Artist */}
                    <div className="change-indicator-field">
                      <ChangeRequestIndicator
                        show={currentTrackChanges?.artists.hasChanges}
                        displayValue={currentTrackChanges?.artists.value}
                        customObjectKeys={['name', 'performerRole']}
                        customClass="track-artist-change-indicator"
                      ></ChangeRequestIndicator>
                      <TrackArtistFormGroup
                        isMultipleTrack={isMultipleTrack}
                        errorBanner={errorBanner}
                        multipleTracksEdit={multipleTracksEdit}
                        trackData={trackData}
                        baseMixedTrack={baseMixedTrack}
                        readonly={readonly}
                        disabled={disabled}
                        handleArtist={handleArtist}
                        areNewMultipleTracks={areNewMultipleTracks}
                        fieldHasErrors={fieldHasErrors}
                        findErrorMessage={findErrorMessage}
                      />
                    </div>

                    {/* DisplayArtist */}
                    <TrackDisplayArtistFormGroup
                      isMultipleTrack={isMultipleTrack}
                      multipleTracksEdit={multipleTracksEdit}
                      trackData={trackData}
                      baseMixedTrack={baseMixedTrack}
                      readonly={readonly}
                    />

                    {/* WriterComposer */}
                    <div className="change-indicator-field">
                      <ChangeRequestIndicator
                        show={currentTrackChanges?.writersComposers.hasChanges}
                        displayValue={
                          currentTrackChanges?.writersComposers.value
                        }
                        customObjectKeys={['name', 'role']}
                        customClass="track-writers-change-indicator"
                      ></ChangeRequestIndicator>
                      <TrackWriterComposerFormGroup
                        isMultipleTrack={isMultipleTrack}
                        multipleTracksEdit={multipleTracksEdit}
                        trackData={trackData}
                        baseMixedTrack={baseMixedTrack}
                        readonly={readonly}
                        disabled={disabled}
                        handleWriterComposer={handleWriterComposer}
                        areNewMultipleTracks={areNewMultipleTracks}
                        fieldHasErrors={fieldHasErrors}
                        fieldValidationState={fieldValidationState}
                      />
                    </div>

                    {/* Genre */}
                    <ChangeRequestIndicator
                      show={currentTrackChanges?.genre.hasChanges}
                      displayValue={currentTrackChanges?.genre.value}
                      customObjectKeys={['masterGenre', 'name']}
                      customSeparator={' > '}
                    ></ChangeRequestIndicator>
                    <TrackGenreFormGroup
                      trackKey={trackKey}
                      isLoadingCatalogs={isLoadingCatalogs}
                      genres={genres ?? []}
                      queryGenres={queryGenres}
                      multipleTracksEdit={multipleTracksEdit}
                      trackData={trackData}
                      baseMixedTrack={baseMixedTrack}
                      readonly={readonly}
                      disabled={disabled}
                      areNewMultipleTracks={areNewMultipleTracks}
                      isMultipleTrack={isMultipleTrack}
                      fieldHasErrors={fieldHasErrors}
                      fieldValidationState={fieldValidationState}
                      findErrorMessage={findErrorMessage}
                      handleInput={handleInput}
                    />

                    {/* AudioLanguage */}
                    <ChangeRequestIndicator
                      show={currentTrackChanges?.audioLanguage.hasChanges}
                      displayValue={
                        audioLanguages?.find(
                          (lang) =>
                            lang.value ===
                            currentTrackChanges?.audioLanguage.value
                        )?.label
                      }
                    ></ChangeRequestIndicator>
                    <TrackAudioLanguageFormGroup
                      trackKey={trackKey}
                      isLoadingCatalogs={isLoadingCatalogs}
                      audioLanguages={audioLanguages}
                      queryAudioLanguages={queryAudioLanguages}
                      multipleTracksEdit={multipleTracksEdit}
                      trackData={trackData}
                      baseMixedTrack={baseMixedTrack}
                      readonly={readonly}
                      disabled={disabled}
                      areNewMultipleTracks={areNewMultipleTracks}
                      isMultipleTrack={isMultipleTrack}
                      fieldHasErrors={fieldHasErrors}
                      fieldValidationState={fieldValidationState}
                      findErrorMessage={findErrorMessage}
                      handleInput={handleInput}
                    />

                    {/* Track Type */}
                    {showTrackType ? (
                      <>
                        <ChangeRequestIndicator
                          show={currentTrackChanges?.trackType?.hasChanges}
                          displayValue={
                            currentTrackChanges?.trackType?.value ===
                            TrackType.AudioOnly
                              ? t('track.trackType.audioOnly')
                              : t('track.trackType.videoAndAudio')
                          }
                        ></ChangeRequestIndicator>
                        <TrackTypeFormGroup
                          trackKey={trackKey}
                          multipleTracksEdit={multipleTracksEdit}
                          trackData={trackData}
                          baseMixedTrack={baseMixedTrack}
                          readonly={readonly}
                          disabled={disabled}
                          areNewMultipleTracks={areNewMultipleTracks}
                          isMultipleTrack={isMultipleTrack}
                          handleInput={handleInput}
                        />
                      </>
                    ) : null}

                    {/* ISRC */}
                    <TrackIsrcFormGroup
                      isMultipleTrack={isMultipleTrack}
                      trackData={trackData}
                      readonly={readonly}
                      disabled={disabled || isChangeRequest}
                      albumState={albumState}
                      displayValueOnly={isChangeRequest}
                      handleInput={handleInput}
                      fieldHasErrors={fieldHasErrors}
                      fieldValidationState={fieldValidationState}
                      findErrorMessage={findErrorMessage}
                    />

                    {/* CopyrightYear */}
                    <ChangeRequestIndicator
                      show={
                        currentTrackChanges?.copyrightYear.hasChanges ||
                        currentTrackChanges?.copyrightLine.hasChanges
                      }
                      displayValue={[
                        currentTrackChanges?.copyrightYear.value,
                        currentTrackChanges?.copyrightLine.value,
                      ]}
                    ></ChangeRequestIndicator>
                    <TrackCopyrightYearFormGroup
                      multipleTracksEdit={multipleTracksEdit}
                      trackData={trackData}
                      baseMixedTrack={baseMixedTrack}
                      readonly={readonly}
                      disabled={disabled}
                      handleInput={handleInput}
                      fieldHasErrors={fieldHasErrors}
                      fieldValidationState={fieldValidationState}
                      findErrorMessage={findErrorMessage}
                      isMultipleTrack={isMultipleTrack}
                      areNewMultipleTracks={areNewMultipleTracks}
                    />

                    {/* PhonographicCopyrightYear */}
                    <ChangeRequestIndicator
                      show={
                        currentTrackChanges?.phonographicCopyrightYear
                          .hasChanges ||
                        currentTrackChanges?.phonographicCopyrightLine
                          .hasChanges
                      }
                      displayValue={[
                        currentTrackChanges?.phonographicCopyrightYear.value,
                        currentTrackChanges?.phonographicCopyrightLine.value,
                      ]}
                    ></ChangeRequestIndicator>
                    <TrackPhonographicCopyrightYearFormGroup
                      multipleTracksEdit={multipleTracksEdit}
                      trackData={trackData}
                      baseMixedTrack={baseMixedTrack}
                      readonly={readonly}
                      disabled={disabled}
                      handleInput={handleInput}
                      fieldHasErrors={fieldHasErrors}
                      fieldValidationState={fieldValidationState}
                      findErrorMessage={findErrorMessage}
                      isMultipleTrack={isMultipleTrack}
                      areNewMultipleTracks={areNewMultipleTracks}
                    />

                    {/* Publishers */}
                    <div className="change-indicator-field">
                      <ChangeRequestIndicator
                        show={currentTrackChanges?.publishers.hasChanges}
                        displayValue={currentTrackChanges?.publishers.value}
                        customObjectKeys={[
                          'name',
                          'performing_Rights_Organization',
                        ]}
                        customClass="track-publisher-change-indicator"
                      ></ChangeRequestIndicator>
                      <TrackPublishersFormGroup
                        multipleTracksEdit={multipleTracksEdit}
                        trackData={trackData}
                        baseMixedTrack={baseMixedTrack}
                        readonly={readonly}
                        disabled={disabled}
                        handlePublisher={handlePublisher}
                        isMultipleTrack={isMultipleTrack}
                        areNewMultipleTracks={areNewMultipleTracks}
                        fieldHasErrors={fieldHasErrors}
                        fieldValidationState={fieldValidationState}
                      />
                    </div>

                    {/* DurationPlaceholder */}
                    <ChangeRequestIndicator
                      show={currentTrackChanges?.duration.hasChanges}
                      displayValue={TimeUtilities.convertSecondsToTimeString(
                        currentTrackChanges?.duration.value
                      )}
                    ></ChangeRequestIndicator>
                    <TrackDurationFormGroup
                      isMultipleTrack={isMultipleTrack}
                      trackData={trackData}
                      readonly={readonly}
                      disabled={disabled}
                      handleDuration={handleDuration}
                      fieldHasErrors={fieldHasErrors}
                      fieldValidationState={fieldValidationState}
                      findErrorMessage={findErrorMessage}
                      trackKey={trackKey}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <div className="edit-modal-spacer"></div>
                <div className="modal-footer-buttons">
                  {!isMultipleTrack ? (
                    <div className="track-navigation float-start">
                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => previousTrack?.()}
                      >
                        <i className="icon fas  fa-chevron-left"></i>
                      </button>
                      <span className="track-navigation-volume">
                        {formatTrackLabel?.(
                          usesSides ?? false,
                          trackData.discOrSideNumber
                        )}
                      </span>
                      <span className="track-navigation-track">
                        {t('track.trackNumber', {
                          trackNumber: trackData.trackNumber.toString(),
                        })}
                      </span>
                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => nextTrack?.()}
                      >
                        <i className="icon fas  fa-chevron-right"></i>
                      </button>
                    </div>
                  ) : null}
                  {!readonly ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary alt"
                        onClick={onCancel}
                        disabled={isSubmitting || disabled}
                      >
                        {t('common.button.cancel')}
                      </button>
                      <button
                        type="submit"
                        data-testid="Save"
                        value="Submit"
                        className="btn btn-primary buttonSave"
                        disabled={isSubmitting || !isTrackValid || disabled}
                      >
                        {t('common.button.save')}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closePopUp}
                    >
                      {t('common.button.close')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </form>
    </>
  );
};

export default TrackEditViewTemplate;
